/* eslint-disable @typescript-eslint/no-floating-promises */
// import initializeGlia from '@medicare-tools/utils/src/glia';
import * as Sentry from '@sentry/gatsby';
import classNames from 'classnames';
import { graphql, useStaticQuery, Script } from 'gatsby';
import { SEOContext } from 'gatsby-plugin-wpgraphql-seo';
import jsYaml from 'js-yaml';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Phone } from '~components/AllianceCallService/AllianceCallService';
import { OepBanner } from '~components/OepBanner/OepBanner';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { PreOepBanner } from '~components/PreOepBanner/PreOepBanner';
import SEOMetaData from '~components/SEOMetaData/SEOMetaData';
import Spinner from '~components/Spinner/Spinner';
import ZipModal from '~components/ZipCodeForm/ModalContainer';
import { getLocationByIpAddress } from '~components/ZipCodeForm/util';
import MarketoForm from '~globals/Marketo/marketoForm';
import { addParamsToEhmpLink, getCookie, callInvoca } from '~globals/common';
import constants from '~globals/constants';
import { RouterLocation } from '~globals/interfaces';
import { isABTestCandidate } from '~globals/isABTestCandidate';
import { isRedirectCandidate } from '~globals/isRedirectCandidate';
import { redirectReturningVisitor } from '~globals/redirectReturningVisitor';
import { cleanStateName, states } from '~globals/states';
import {
    initializeTracking,
    firePageViewEvent,
    fireTrackEvent,
    fireOpenModal,
} from '~globals/tracking';
import { getSession } from '~globals/user';
import { withinDateRange } from '~globals/withinDateRange';
import useFeatureFlags from '~hooks/featureFlags/useFeatureFlags';
import useAdobeTarget from '~hooks/useAdobeTarget';
import useCustomerCenter from '~hooks/useCustomerCenter';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import useSelectedAllianceId from '~hooks/useSelectedAllianceId';
import { toggleZipFormModal } from '~redux/Actions/DisplayActions';
import {
    updateFilter,
    updateGeoLocation,
    updateMarketoFormId,
    updateMultiCountyList,
    updateNtmVisited,
    updatePreOEPStateConfig,
    updatePhone,
    updatePlanType,
    updateTrackingParams,
    updateTrackingStatus,
    updateZipAndCounty,
    updateZipModalDestination,
    updateOEPStateConfig,
    updateOEPStatePeriod,
} from '~redux/Actions/GlobalActions';
import { updateSavedSession } from '~redux/Actions/UserActions';

type Props = {
    location: RouterLocation;
};

interface LeadConfigData {
    leadForm: {
        [key: string]: LeadFormData;
    };
    banner: {
        [key: string]: BannerData[];
    };
    periods: {
        [key: string]: PeriodData;
    };
}

const Layout = ({
    location: { pathname, hash, key, search, href },
    children,
}: React.PropsWithChildren<Props>) => {
    const { tags } = usePageContext();
    const { adobeHasLoaded, adobeExperiences } = useAdobeTarget();
    const { selectedAllid, urlParams } = useSelectedAllianceId(search);
    const enableCustomerCenter = useFeatureFlags().CustomerCenter();
    const enableMedicareTools = useFeatureFlags().MedicareTools();
    const enableZipModal = process.env.GATSBY_EH_ENV === 'local' || useFeatureFlags().ZipModal();

    const dispatch = useAppDispatch();

    const isStatesPage = useMemo(() => pathname.includes('-health-insurance/'), [pathname]);
    const isLargeEmpPage = useMemo(() => pathname.includes('/large-group/'), [pathname]);
    const isLargeEmpLeadFormPage = useMemo(() => pathname.includes('/large-group-lead-form/'), [pathname]);
    const state = useMemo(
        () => pathname.replace(/.*\/([a-zA-Z-]+)-health-insurance\//g, '$1'),
        [pathname]
    );

    const allid = useAppSelector((state) => state.global.allianceId);
    const gclid = useAppSelector((state) => state.global.gclid);
    const leadId = useAppSelector((state) => state.global.leadId);
    const toggleActive = useAppSelector((state) => state.display.toggleActive);
    const leadToken = useAppSelector((state) => state.global.leadToken);
    const mktCode = useAppSelector((state) => state.global.mktCode);
    const msclid = useAppSelector((state) => state.global.msclid);
    const ntmVisited = useAppSelector((state) => state.global.ntmVisited);
    const throttleValue = useAppSelector((state) => state.global.throttle);
    const trackingParams = useAppSelector((state) => state.global.trackingParams);

    const { countyName, zip } = useAppSelector((state) => state.global.geoLocation);

    const planYear = useAppSelector((state) => state.global.planYear);
    const sessionCode = useAppSelector((state) => state.user.savedSession.sessionCode) ?? '';

    const user = useAppSelector((state) => state.user.user);

    const [isOEP, setOEP] = useState(false);
    const [isPreOEP, setPreOEP] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [ipState, setIpState] = useState<string | null>(null);

    const clickIds: { [key: string]: unknown } = {
        gclid: gclid ? JSON.parse(gclid) : null,
        msclkid: msclid ? JSON.parse(msclid) : null,
        mkt_code: mktCode ? JSON.parse(mktCode) : null,
    };
    const queryParams = {} as Record<
        EhmTrackingParameters | OtherTrackingParameters | ClickIds,
        string
    >;

    // this will render page during SSG with a loader
    // using this to prevent flicker showing during A/B tests (pre-render to hydration state flicker)
    // site performance metrics (LCP, FCP) are negatively affected by this approach though
    const isPagePrerenderedWithLoader = ['/enroll/online/', '/enroll/welcome-back/'].some((path) =>
        pathname.includes(path)
    );

    const isSutterLandingPage = ['/partner/sutter/'].some((path) => pathname.includes(path));
    const additionalTemplateClass = isSutterLandingPage ? 'template-sutter-lp' : 'template-default';

    [
        ...constants.OTHER_TRACKING_PARAMETERS,
        ...constants.EHM_TRACKING_PARAMETERS,
        ...(Object.keys(clickIds) as ClickIds[]),
    ].forEach((p) => {
        if (typeof urlParams[p] !== 'undefined') {
            queryParams[p] = urlParams[p];
        }
    });

    // redirect returning visitor to quote page if conditions are met and show the spinner
    const isRedirectionActivePage =
        typeof window !== 'undefined'
            ? redirectReturningVisitor(getCookie('profile.visitedQuotePage') as string, {
                pathname: pathname,
                trackingParams: trackingParams,
                allid: selectedAllid,
                zip: zip,
                leadToken: leadToken,
                countyName: countyName,
                ntmVisited: ntmVisited,
                sessionCode: sessionCode,
            })
                ? true
                : false
            : false;

    // determine if this page should be participating in A/B test
    // for server-rendered version show the spinner only for pages that are spec'd by isPagePrerenderedWithLoader
    const isABTestActivePage = () => {
        if (typeof window !== 'undefined') {
            switch (true) {
                case isRedirectCandidate(pathname):
                    return true;
                case isABTestCandidate(pathname):
                    return true;
                case isRedirectionActivePage:
                    return true;
                default:
                    return false;
            }
        }

        return isPagePrerenderedWithLoader ? true : false;
    };

    const handleLinkClick = (e: Event) => {
        const { currentTarget } = e as MouseEvent;
        const { filter, skipZip, planname } = (currentTarget as HTMLAnchorElement).dataset;
        if (planname) {
            if (planname.includes('Advantage')) {
                dispatch(updatePlanType('MA'));
            } else if (planname.includes('Supplement')) {
                dispatch(updatePlanType('MS'));
            } else if (planname.includes('Part D')) {
                dispatch(updatePlanType('MD'));
            }
        }
        if (enableZipModal && !zip && skipZip !== 'true') {
            if (filter) {
                dispatch(updateFilter(filter));
            }
            e.preventDefault();
            dispatch(updateZipModalDestination((currentTarget as HTMLAnchorElement).href));
            dispatch(toggleZipFormModal(true));
            fireOpenModal();
            firePageViewEvent({
                sysEnv: { siteGroup: 'EHI' },
                page: {
                    name: 'zip search',
                    section: 'zip tool',
                },
                events: ['toolstart'],
            });
            return;
        }

        addParamsToEhmpLink(
            currentTarget as HTMLAnchorElement,
            {
                ...trackingParams,
                zip: zip,
                county: countyName,
                allid: allid,
                leadToken: leadToken,
                action: 'redirect_with_zip_code',
                lastV1Action: 'quoteFromV1',
                ...(filter && { filter: filter }),
                ...(ntmVisited.length > 0 && { n2m: 'true' }),
                ...(sessionCode && { ppSaveSessionId: sessionCode }),
            },
            user.isLoggedIn
        );
    };

    const globalSeo = useStaticQuery<{ wp: { seo: GlobalSeo } }>(graphql`
        query SiteInfoQuery {
            wp {
                seo {
                    contentTypes {
                        post {
                            title
                            schemaType
                            metaRobotsNoindex
                            metaDesc
                        }
                        page {
                            metaDesc
                            metaRobotsNoindex
                            schemaType
                            title
                        }
                    }
                    webmaster {
                        googleVerify
                        yandexVerify
                        msVerify
                        baiduVerify
                    }
                    schema {
                        companyName
                        personName
                        companyOrPerson
                        wordpressSiteName
                        siteUrl
                        siteName
                        inLanguage
                        logo {
                            sourceUrl
                            mediaItemUrl
                            altText
                        }
                    }
                    social {
                        facebook {
                            url
                            defaultImage {
                                sourceUrl
                                mediaItemUrl
                            }
                        }
                        instagram {
                            url
                        }
                        linkedIn {
                            url
                        }
                        mySpace {
                            url
                        }
                        pinterest {
                            url
                            metaTag
                        }
                        twitter {
                            username
                        }
                        wikipedia {
                            url
                        }
                        youTube {
                            url
                        }
                    }
                }
            }
        }
    `)?.wp.seo;

    const getPotentialProdType = useCallback(() => {
        let prodType = 'SMB';
        const pageTags = tags?.map((tag) => tag.slug);

        switch (true) {
            case isStatesPage:
                prodType = 'IFP';
                break;
            case pathname.includes('/medicare-supplement/') ||
                (pageTags?.length && pageTags.includes('type-ms')):
                prodType = 'MS';
                break;
            default:
                prodType = 'SMB';
                break;
        }

        return prodType;
    }, [isStatesPage]);

    // Marketo Lead Config for IFP State pages
    const getLeadConfig = useCallback(async () => {
        try {
            const req = await fetch(
                `https://www.${process.env.GATSBY_SUBDOMAIN || ''}ehealthinsurance.com/ifp-api/misc/instant-config/yaml/affordable-health-insurance-by-state-lead-form`
            );

            if (!req.ok) {
                throw `${req.status} - ${req.statusText}`;
            }

            const resp = await req.text();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const data: LeadConfigData = jsYaml.load(resp);
            const leadFormConfig =
                data && ((state && data.leadForm[state]) || data.leadForm['DEFAULT']);
            dispatch(updateMarketoFormId(leadFormConfig.marketoFormId));
            dispatch(updateTrackingStatus(leadFormConfig.trackingStatus));
        } catch (err: unknown) {
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
                console.error('Lead Config error:', err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
                console.error('Lead Config error:', err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'ZipEmailLeadResponseData',
                        event: 'getLeadConfig',
                    },
                });
            }
        }
    }, []);
    // Get OEP Config by State
    const getOepConfig = useCallback(async () => {
        try {
            const stateName = cleanStateName(state);
            const stateItem = states.filter((item) => item.name.toLowerCase() === stateName)[0];
            const regionCode = stateItem ? stateItem.abbreviation : null;

            if (!regionCode) throw `Unable to get location`;

            setIpState(regionCode);

            const req = await fetch(
                `https://www.${process.env.GATSBY_SUBDOMAIN || ''}ehealthinsurance.com/ifp-api/misc/instant-config/yaml/oep`
            );

            if (!req.ok) {
                throw `${req.status} - ${req.statusText}`;
            }

            const resp = await req.text();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const data: LeadConfigData = jsYaml.load(resp);
            const preOEPStateConfig =
                data && ((regionCode && data.leadForm[regionCode]) || data.leadForm['DEFAULT']);
            const oepStateConfig =
                data && ((regionCode && data.banner[regionCode]) || data.banner['DEFAULT']);
            const oepStateActive =
                data && ((regionCode && data.periods[regionCode]) || data.periods['DEFAULT']);
            const dates = preOEPStateConfig.when;
            const today = new Date();
            const preStartDate = new Date(dates[0]);
            const preEndDate = new Date(dates[1]);
            const isPreActive = withinDateRange(today, preStartDate, preEndDate);

            const showPreOEP = !(getCookie('leadForm.closed') === 'true');
            setPreOEP(() => isPreActive && showPreOEP);
            dispatch(updatePreOEPStateConfig(preOEPStateConfig));

            const oepStartDate = new Date(oepStateActive.from);
            const oepEndDate = new Date(oepStateActive.to);
            const isOepActive = withinDateRange(today, oepStartDate, oepEndDate);

            setOEP(isOepActive);
            dispatch(updateOEPStatePeriod(oepStateActive));
            dispatch(updateOEPStateConfig(oepStateConfig));
        } catch (err: unknown) {
            let sentryError;
            if (typeof err === 'string') {
                sentryError = new Error(err);
                console.error('OEP State Config error:', err);
            } else if (err instanceof Error) {
                sentryError = new Error(err.message);
                console.error('OEP State Config error:', err.message);
            }
            if (process.env.GATSBY_EH_ENV === 'prod') {
                Sentry.captureException(sentryError, {
                    tags: {
                        site: process.env.GATSBY_SITE_NAME,
                        component: 'ZipEmailLeadResponseData',
                        event: 'getOepConfig',
                    },
                });
            }
        }
    }, []);

    useEffect(() => {
        if (isStatesPage) {
            getLeadConfig();
            getOepConfig();

            const state = pathname.replace(/^.*\/([^/]+)\/?$/, '$1');
            firePageViewEvent({
                sysEnv: { siteGroup: 'IFP' },
                page: { name: `Landing: ${state}`, section: 'SEO' },
            });
        } else if (isLargeEmpPage) {
            firePageViewEvent({
                sysEnv: { siteGroup: 'EHI' },
                page: { name: 'enroll:large-group', section: 'online' },
            });
        } else if (isLargeEmpLeadFormPage) {
            console.log('Loading Large Group Lead Form...');

            firePageViewEvent({
                sysEnv: { siteGroup: 'EHI' },
                page: { name: 'enroll:large-group-lead-form', section: 'online' },
            });
        }
    }, [isStatesPage, isLargeEmpPage]);

    useEffect(() => {
        dispatch(updateFilter(''));
    }, []);

    useEffect(() => {
        window.addEventListener(
            'createLead',
            (e: CustomEventInit<{ email: string; formId: number }>) => {
                if (e.detail?.formId) {
                    const mkto = new MarketoForm(e.detail.formId);
                    mkto.init();
                    mkto.mktoFormSubmit(e.detail.email, allid);
                }
            }
        );
    }, []);

    const { ppSaveSessionId, al: alToken } = urlParams;
    enableCustomerCenter && useCustomerCenter(ppSaveSessionId, alToken);
    useEffect(() => {
        if (ppSaveSessionId && ppSaveSessionId !== sessionCode && enableMedicareTools) {
            getSession(
                { dispatch, updateSavedSession, updateZipAndCounty },
                {
                    allid: selectedAllid,
                    planYear: planYear,
                    sessionCode: ppSaveSessionId,
                }
            ).catch((e) => {
                console.error(e);
            });
        }
    }, []);

    useEffect(() => {
        if (sessionCode) {
            const url = new URL(window.location.href);
            url.searchParams.set('ppSaveSessionId', sessionCode);
            const newUrl = url.toString();
            if (window.location.href !== newUrl) {
                window.history.replaceState(null, '', newUrl);
            }
        }
    }, [sessionCode]);

    useEffect(() => {
        if (hash === '') {
            window.scrollTo(0, 0);
        } else {
            setTimeout(() => {
                const id = hash.replace('#', '');
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 0);
        }

        // let intervalId: NodeJS.Timer;
        let counter = 0;
        const intervalId = setInterval(() => {
            counter++;
            if (counter >= 50) {
                clearInterval(intervalId);
                return;
            }
            const banner = document.getElementById('pre-aep-banner');
            if (banner) {
                banner.onclick = handleLinkClick;
                clearInterval(intervalId);
            }
        }, 100);
    }, [pathname, hash, key]);

    useEffect(() => {
        initializeTracking(selectedAllid, '');
    }, [selectedAllid]);

    useEffect(() => {
        initializeTracking('', leadId);
    }, [leadId]);

    // set a timer to remove `at-hide` class in case Adobe Target doesn't fire
    const [atHideClass, setAtHideClass] = useState('at-hide');
    useEffect(() => {
        const atHideClasstimer = setTimeout(() => {
            setAtHideClass('at-hide-off');
        }, 5000);
        return () => clearTimeout(atHideClasstimer);
    }, []);

    useEffect(() => {
        document.querySelectorAll('a').forEach((link) => {
            if (link.href.includes(process.env.GATSBY_EHMP_URL)) {
                link.onclick = handleLinkClick;
                link.onkeydown = (e) => {
                    // reevaluate: this is probably doing unnecessary extra work
                    if (e.key === ' ') handleLinkClick(e);
                };
            }
        });
    }, [
        adobeHasLoaded,
        allid,
        leadId,
        leadToken,
        ntmVisited,
        sessionCode,
        trackingParams,
        user,
        zip,
        countyName,
    ]);

    useEffect(() => {
        const setupGlia = async () => {
            // await initializeGlia();
        };
        setupGlia().catch(console.error);
    }, []);

    // Look for generic MEDI-XXXX experience test data to be retruned and hide the anti-flicker
    useEffect(() => {
        // unhide the preloader only if the experience name starts with `medi-` string and
        // this it is *not* a redirection offer (during redirection keep the preloader on until redirected to target URL)
        const unhideAtHideClass: boolean = adobeExperiences.find(
            (experience: { testName: string; testVariant: string; testIsRedirect: boolean }) => {
                return experience.testName?.includes('medi-') && !experience.testIsRedirect;
            }
        )
            ? true
            : false;
        setAtHideClass(unhideAtHideClass ? 'at-hide-off' : 'at-hide');
    }, [adobeExperiences]);

    useEffect(() => {
        if (pathname === '/medicare/new-to-medicare/' || pathname === '/new-to-medicare/') {
            dispatch(updateNtmVisited('NTM homepage'));
        }
        dispatch(updateTrackingParams(queryParams));
    }, []);

    useEffect(() => {
        window.onInvocaComplete = (batchId, mapping) => {
            if (mapping?.length > 0) {
                const numberResponse = mapping.find(
                    (item) => item.status === 'success' && item.formattedNumber
                );
                numberResponse &&
                    dispatch(
                        updatePhone({
                            number: numberResponse.formattedNumber,
                            isFinal: true,
                        })
                    );
            }
        };

        window.InvocaVars = window.InvocaVars || {};
        window.InvocaVars.plan_type = getPotentialProdType();
        callInvoca();
    }, []);

    useEffect(() => {
        const getLocation = async () => {
            try {
                const geoLocation = await getLocationByIpAddress();
                if (geoLocation) {
                    const { locationList } = geoLocation.data.locationByIpAddress;
                    if (locationList) {
                        dispatch(updateGeoLocation(locationList[0]));
                        dispatch(updateMultiCountyList(locationList));
                    }
                }
            } catch (e) {
                console.error(e);
            }
        };
        if (!countyName) {
            void getLocation();
        }
    }, []);

    const [currentScroll, setCurrentScroll] = useState(0);
    const [hasScrolled, setHasScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => {
            setCurrentScroll(window.scrollY);
            if (window.scrollY > 500) {
                setHasScrolled(true);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [currentScroll]);
    useEffect(() => {
        if (hasScrolled === true) {
            fireTrackEvent({ page: { action: `scroll: vertical` } });
        }
    }, [hasScrolled]);

    // TODO: _possibly_ create custom block with both blocks encapsulated
    useEffect(() => {
        const stateDrawer = document.querySelector<HTMLDivElement>('div.state-drawer > ul');
        const isActive = toggleActive !== undefined ? toggleActive : false;

        stateDrawer?.setAttribute('data-active', isActive);
    }, [toggleActive]);

    useEffect(() => {
        const comparePlansCTAs = document.querySelectorAll<HTMLButtonElement>('.compare-plans-cta');

        const handleClick = (evt: MouseEvent) => {
            evt.preventDefault();
            dispatch(toggleZipFormModal(true));
        };

        comparePlansCTAs.forEach((button) => {
            button.addEventListener('click', handleClick);
        });

        // Cleanup function to remove all the event listeners
        return () => {
            comparePlansCTAs.forEach((button) => {
                button.removeEventListener('click', handleClick);
            });
        };
    }, [dispatch]);

    useEffect(() => {
        const comparePlans = document.querySelectorAll<HTMLButtonElement>('.compare-plans-cta');

        const handleClick = (index: number) => () => {
            fireTrackEvent({ page: { action: `Module${index + 1}: Compare plans` } });
        };

        comparePlans.forEach((button, index) => {
            button.addEventListener('click', handleClick(index));
            button.dataset.listenerAdded = 'true';
        });

        return () => {
            comparePlans.forEach((button, index) => {
                button.removeEventListener('click', handleClick(index));
            });
        };
    }, []);

    return (
        <SEOContext.Provider value={{ global: globalSeo }}>
            {/* SEO Meta Updates -- this is causing Helmet nesting issues */}
            <SEOMetaData href={href} global={globalSeo} />

            {/* Update <body> tag with extra CSS classes */}
            <Helmet htmlAttributes={{ lang: 'en-US' }}>
                <body
                    className={`throttle-${throttleValue} ${additionalTemplateClass}`}
                    data-theme="ehim"
                    data-preoep={isPreOEP}
                />
            </Helmet>

            {isPreOEP && isStatesPage && <PreOepBanner setPreOEP={setPreOEP} />}

            {/* Spinner loading indicator if this is an active A/B test page */}
            {isABTestActivePage() && (
                <div className={`spinner-${atHideClass} flex align-center mt-48 md:mt-60 lg:mt-80`}>
                    <Spinner color="primary" size="large" />
                </div>
            )}

            {/* EHI Header */}
            {isABTestActivePage() ? (
                <div className={`${atHideClass}`}>
                    <div className="header-wrapper bg-white">
                        {/* Allowed parameters for header web component: allianceId, isStatePage, page, allianceInfo, navLinks, jwtToken, sticky, menuOverlayInheritMarginTop, activetab, useOldLogo, ichra */}
                        <ehi-header
                            allianceId={allid}
                            page="home"
                            menuOverlayInheritMarginTop={'N'}
                            activetab={`tab-0`}
                            version="v2"
                        >
                            <div
                                slot="invoca-header--mobile"
                                className="invoca-phone-number phone-number slot-phone-number"
                            >
                                <Phone />
                            </div>
                            <div
                                slot="invoca-header--tablet"
                                className="invoca-phone-number phone-number slot-phone-number"
                            >
                                <Phone />
                            </div>
                            <div
                                slot="invoca-header--desktop"
                                className="invoca-phone-number phone-number slot-phone-number"
                            >
                                <Phone />
                            </div>
                        </ehi-header>
                    </div>
                </div>
            ) : (
                <div className="header-wrapper bg-white">
                    {/* Allowed parameters for header web component: allianceId, isStatePage, page, allianceInfo, navLinks, jwtToken, sticky, menuOverlayInheritMarginTop, activetab, useOldLogo, ichra */}
                    <ehi-header
                        allianceId={allid}
                        page="home"
                        menuOverlayInheritMarginTop={'N'}
                        activetab={`tab-0`}
                        version="v2"
                    >
                        <div
                            slot="invoca-header--mobile"
                            className="invoca-phone-number phone-number slot-phone-number"
                        >
                            <Phone />
                        </div>
                        <div
                            slot="invoca-header--tablet"
                            className="invoca-phone-number phone-number slot-phone-number"
                        >
                            <Phone />
                        </div>
                        <div
                            slot="invoca-header--desktop"
                            className="invoca-phone-number phone-number slot-phone-number"
                        >
                            <Phone />
                        </div>
                    </ehi-header>
                </div>
            )}

            {isOEP && isStatesPage && <OepBanner />}

            {/* Main content element (with hiding mechanism if an active A/B page) */}
            <main
                id="maincontent"
                className={classNames(isABTestActivePage() ? `${atHideClass}` : '')}
            >
                <div data-name="login-widget">
                    <div id="loginwidget"></div>
                </div>

                {children}

                {/* <PopupCtaListener /> */}
                <ZipModal />
            </main>

            {/* EHI Footer */}
            {isABTestActivePage() ? (
                <div className={`${atHideClass}`}>
                    <div className="footer-wrapper bg-surface-primary-darker">
                        {/* Allowed parameters for footer web component: allianceId, page, showMedDisclaimer */}
                        <ehi-footer
                            allianceId={allid}
                            page="home"
                            showMedDisclaimer={false}
                            version="v2"
                        ></ehi-footer>
                    </div>
                </div>
            ) : (
                <div className="footer-wrapper bg-surface-primary-darker">
                    {/* Allowed parameters for footer web component: allianceId, page, showMedDisclaimer */}
                    <ehi-footer
                        allianceId={allid}
                        page="home"
                        showMedDisclaimer={false}
                        version="v2"
                    ></ehi-footer>
                </div>
            )}

            {/* Invoca Initialization Script */}
            <Script id="invoca_initialization">
                {`
                    var WA_ENV = 'QA';
                    if (location.host === 'www.ehealthinsurance.com') {
                        WA_ENV = 'PRD';
                    } else if (location.host === 'www.cm.ehealthinsurance.com') {
                        WA_ENV = 'CM';
                    }
                    window._waStatic = { ...window._waStatic, invoca: { site: 'EHI', env: WA_ENV } };
                    function onCompleteOverride(batchId, mapping) {
                        if (window.onInvocaComplete) {
                            window.onInvocaComplete(batchId, mapping);
                        } else {
                            setTimeout(function () {
                                onCompleteOverride(batchId, mapping);
                            }, 200);
                        }
                    }
                `}
            </Script>

            {/* Marketo Quarantine */}
            <div className="marketo-quarantine" aria-hidden="true"></div>
        </SEOContext.Provider>
    );
};

export default Layout;
